'use client';

// Shared play logic for the standalone mini-games (stars-mini, fields-mini). These are tiny,
// persistence-free versions of the main games: a board, undo history, and the Check / Hint /
// auto-check feedback loop — no store, no save, no server. useMiniGame holds that whole state
// machine, parameterised by a small per-game config that mirrors GameConfig. It is deliberately
// UI-free (no highlight, no components): the surrounding chrome lives in @puzzlio/ui's MiniGameShell.

import { useCallback, useEffect, useRef, useState } from 'react';
import isEqual from 'lodash/isEqual';
import { BoardMessage } from '../types';
import { firstCheckClickMessage, noErrorsMessage, nothingToCheckMessage } from './playState';

export type MiniGamePressedButton = 'hint' | 'check' | 'none';

/**
 * The only thing handleCheck reads off a click: alt-click is the dev shortcut that jumps the board to
 * an almost-complete state. Structural, so a DOM MouseEvent still satisfies it, but core stays free of
 * DOM types and a native caller can pass nothing at all.
 */
export type CheckModifiers = { altKey?: boolean };

export interface MiniGameConfig<CellT> {
  /** Fresh empty board for the current puzzle. */
  getInitialCells: () => CellT[][];
  /** Validate the board, returning one message per error (empty when clean). */
  checkBoard: (cells: CellT[][]) => BoardMessage[];
  /** Board is fully filled in — arms the delayed auto-check reminder. */
  isBoardFull: (cells: CellT[][]) => boolean;
  /** Board has enough placed to possibly be a solution — triggers a completion check. */
  canBeComplete: (cells: CellT[][]) => boolean;
  /** Alt-click debug helper: a near-solved board, or null when unavailable. */
  createAlmostCompleteState: () => CellT[][] | null;
  /** Optional hint generator; when omitted the game exposes no Hint button. */
  generateHint?: (cells: CellT[][]) => BoardMessage;
}

/** The slice of useMiniGame's return that MiniGameShell renders (everything but the grid wiring). */
export interface MiniGameControls {
  complete: boolean;
  messages: BoardMessage[];
  pressedButton: MiniGamePressedButton;
  hasHint: boolean;
  handleUndo: () => void;
  handleHint: () => void;
  handleCheck: (e?: CheckModifiers) => void;
}

export interface MiniGameState<CellT> extends MiniGameControls {
  cells: CellT[][];
  handleSetCell: (row: number, col: number, cell: CellT) => void;
  handleSetCells: (cells: CellT[][]) => void;
}

export function useMiniGame<CellT>(date: string, config: MiniGameConfig<CellT>): MiniGameState<CellT> {
  // The config closes over the current puzzle; keep the latest in a ref so the event handlers below
  // always read fresh game logic without listing every closed-over value as a dependency.
  const configRef = useRef(config);
  configRef.current = config;

  const [cells, setCells] = useState<CellT[][]>(() => config.getInitialCells());
  const [, setHistory] = useState<CellT[][][]>([]);
  const [messages, setMessages] = useState<BoardMessage[]>([]);
  const [complete, setComplete] = useState(false);
  const [hasCheckBeenPressed, setHasCheckBeenPressed] = useState(false);
  const [pressedButton, setPressedButton] = useState<MiniGamePressedButton>('none');
  const autoCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFeedback = useCallback(() => {
    setMessages([]);
    setHasCheckBeenPressed(false);
    setPressedButton('none');
  }, []);

  // Reset all play state whenever the board (date) changes.
  useEffect(() => {
    if (autoCheckTimeoutRef.current) clearTimeout(autoCheckTimeoutRef.current);
    setCells(configRef.current.getInitialCells());
    setHistory([]);
    setComplete(false);
    clearFeedback();
  }, [date, clearFeedback]);

  const showFirstCheckError = useCallback(() => {
    setPressedButton('check');
    setMessages([firstCheckClickMessage]);
    setHasCheckBeenPressed(true);
  }, []);

  const handleSetCell = useCallback(
    (row: number, col: number, cell: CellT) => {
      if (complete) return;
      const cfg = configRef.current;

      const newCells = cells.map((r, i) => r.map((c, j) => (i === row && j === col ? cell : c)));
      setHistory((h) => [...h, cells]);
      setCells(newCells);

      if (autoCheckTimeoutRef.current) clearTimeout(autoCheckTimeoutRef.current);

      const full = cfg.isBoardFull(newCells);
      const canComplete = cfg.canBeComplete(newCells);
      if (full || canComplete) {
        const errors = cfg.checkBoard(newCells);
        if (errors.length === 0 && canComplete) {
          setComplete(true);
          setMessages([]);
        } else if (full && errors.length > 0) {
          // Delay revealing errors so the player has a chance to keep cycling the cell.
          autoCheckTimeoutRef.current = setTimeout(() => {
            if (cfg.checkBoard(newCells).length > 0) showFirstCheckError();
          }, 1000);
        }
      } else {
        clearFeedback();
      }
    },
    [cells, complete, clearFeedback, showFirstCheckError]
  );

  // Bulk cell update used by the grid's highlight (and drag) path. Highlighting only flips highlight
  // bits, so it never triggers completion — just record history and apply.
  const handleSetCells = useCallback(
    (newCells: CellT[][]) => {
      if (complete) return;
      setHistory((h) => [...h, cells]);
      setCells(newCells);
    },
    [cells, complete]
  );

  const handleUndo = useCallback(() => {
    setHistory((h) => {
      if (h.length === 0) return h;
      setCells(h[h.length - 1]);
      clearFeedback();
      return h.slice(0, -1);
    });
  }, [clearFeedback]);

  const handleHint = useCallback(() => {
    const cfg = configRef.current;
    if (!cfg.generateHint) return;
    const errors = cfg.checkBoard(cells);
    if (errors.length > 0) {
      setPressedButton('check');
      setMessages(errors);
    } else {
      setPressedButton('hint');
      setMessages([cfg.generateHint(cells)]);
    }
  }, [cells]);

  const handleCheck = useCallback(
    (e?: CheckModifiers) => {
      const cfg = configRef.current;
      if (e?.altKey) {
        const almostComplete = cfg.createAlmostCompleteState();
        if (almostComplete) {
          setHistory((h) => [...h, cells]);
          setCells(almostComplete);
          clearFeedback();
        }
        return;
      }
      if (isEqual(cells, cfg.getInitialCells())) {
        setPressedButton('check');
        setMessages([nothingToCheckMessage]);
        return;
      }
      const errors = cfg.checkBoard(cells);
      if (errors.length === 0) {
        setPressedButton('none');
        setMessages([noErrorsMessage]);
      } else if (!hasCheckBeenPressed) {
        showFirstCheckError();
      } else {
        setPressedButton('check');
        setMessages(errors);
      }
    },
    [cells, hasCheckBeenPressed, clearFeedback, showFirstCheckError]
  );

  return {
    cells,
    messages,
    complete,
    pressedButton,
    hasHint: !!config.generateHint,
    handleSetCell,
    handleSetCells,
    handleUndo,
    handleHint,
    handleCheck,
  };
}
